import { useEffect, type Dispatch, type SetStateAction } from 'react';
import type { Company, Group, IndustryType, Unit, UnitFilterData } from '@/types';
import { MODEL_LABELS } from '@/lib/constants';
import {
  CACHE_KEY_BASE,
  validateAndRestoreIndustryTypes,
  getCachedData,
  setCachedData,
} from '../FilterContext';

interface FilterCacheRestoreArgs {
  user: { id: string } | null | undefined;
  rbacReady: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setCompanies: Dispatch<SetStateAction<Company[]>>;
  setGroups: Dispatch<SetStateAction<Group[]>>;
  setUnitsInternal: Dispatch<SetStateAction<(Unit | UnitFilterData)[]>>;
  setSelectedCompanyIds: Dispatch<SetStateAction<string[]>>;
  setSelectedGroupIds: Dispatch<SetStateAction<string[]>>;
  setSelectedModels: Dispatch<SetStateAction<string[]>>;
  setSelectedStatuses: Dispatch<SetStateAction<string[]>>;
  setSelectedIndustryTypes: Dispatch<SetStateAction<IndustryType[]>>;
}

interface FilterSelectionSyncArgs {
  user: { id: string } | null | undefined;
  hasInitialLoad: boolean;
  selectedCompanyIds: string[];
  selectedGroupIds: string[];
  selectedModels: string[];
  selectedStatuses: string[];
  selectedIndustryTypes: IndustryType[];
}

/**
 * One-shot, user-scoped restore of filter state from cache, extracted verbatim
 * from FilterProvider (ISS-030). MUST be called BEFORE the data-loader effects
 * so effect-registration order is unchanged: the loaders' cache-valid branch
 * reads localStorage synchronously, and the sync effects (below) must still run
 * AFTER the loaders on any given commit, exactly as before.
 */
export function useFilterCacheRestore({
  user,
  rbacReady,
  setIsLoading,
  setCompanies,
  setGroups,
  setUnitsInternal,
  setSelectedCompanyIds,
  setSelectedGroupIds,
  setSelectedModels,
  setSelectedStatuses,
  setSelectedIndustryTypes,
}: FilterCacheRestoreArgs) {
  // CRITICAL: Load from user-specific cache ONLY after rbacReady
  // This prevents the flash of unauthorized content
  useEffect(() => {
    if (!rbacReady || !user?.id) {
      // Reset to loading state until RBAC is ready
      setIsLoading(true);
      return;
    }

    // Now safe to load from user-specific cache
    const userId = user.id;
    console.log('[FilterContext] rbacReady, loading user-specific cache for:', userId);

    const cachedCompanies = getCachedData<Company[]>(CACHE_KEY_BASE.companies, userId);
    const cachedGroups = getCachedData<Group[]>(CACHE_KEY_BASE.groups, userId);
    const cachedSelectedCompanyIds = getCachedData<string[]>(CACHE_KEY_BASE.selectedCompanyIds, userId);
    const cachedSelectedGroupIds = getCachedData<string[]>(CACHE_KEY_BASE.selectedGroupIds, userId);
    const cachedSelectedModels = getCachedData<string[]>(CACHE_KEY_BASE.selectedModels, userId);
    const cachedSelectedStatuses = getCachedData<string[]>(CACHE_KEY_BASE.selectedStatuses, userId);
    const cachedSelectedIndustryTypes = getCachedData<IndustryType[]>('company_filter_selected_industry_types', userId);

    if (cachedCompanies) setCompanies(cachedCompanies);
    if (cachedGroups) setGroups(cachedGroups);

    // PERFORMANCE: Restore cached units for instant badge counts
    // Uses setUnitsInternal directly - the exposed setUnits already wraps with startTransition
    const cachedUnits = getCachedData<UnitFilterData[]>(CACHE_KEY_BASE.units, userId);
    if (cachedUnits && cachedUnits.length > 0) {
      setUnitsInternal(cachedUnits);
      console.log('✅ [FilterContext] Restored cached units for instant filter counts:', cachedUnits.length);
    }

    // Validate cached selections against actual cached data to prevent stale ID errors
    // Only set selectedCompanyIds if they exist in cachedCompanies
    if (cachedSelectedCompanyIds && cachedCompanies) {
      const validCompanyIds = new Set(cachedCompanies.map(a => a.id));
      const validSelectedCompanyIds = cachedSelectedCompanyIds.filter(id => validCompanyIds.has(id));
      if (validSelectedCompanyIds.length > 0) {
        setSelectedCompanyIds(validSelectedCompanyIds);
      } else if (cachedCompanies.length > 0) {
        // All cached selections were invalid, select all available companies
        setSelectedCompanyIds(cachedCompanies.map(a => a.id));
      }
    } else if (cachedSelectedCompanyIds) {
      // No cached companys to validate against, use selections as-is (will be validated later)
      setSelectedCompanyIds(cachedSelectedCompanyIds);
    }

    // Validate cached group selections against actual cached groups
    if (cachedSelectedGroupIds && cachedGroups) {
      const validGroupIds = new Set(cachedGroups.map(g => g.id));
      // Also allow __unassigned_* pseudo-IDs
      const validSelectedGroupIds = cachedSelectedGroupIds.filter(id =>
        validGroupIds.has(id) || id.startsWith('__unassigned_')
      );
      if (validSelectedGroupIds.length > 0) {
        setSelectedGroupIds(validSelectedGroupIds);
      }
    } else if (cachedSelectedGroupIds) {
      setSelectedGroupIds(cachedSelectedGroupIds);
    }

    // Restore cached models if array has items, otherwise explicitly set all models selected
    if (cachedSelectedModels && cachedSelectedModels.length > 0) {
      setSelectedModels(cachedSelectedModels);
    } else {
      // No cache or empty cache - explicitly set all models selected (first login default)
      const allModels = Object.keys(MODEL_LABELS);
      setSelectedModels(allModels);
      setCachedData(CACHE_KEY_BASE.selectedModels, userId, allModels);
      console.log('✅ [FilterContext] Auto-selected all models for first login:', allModels.length);
    }
    // Only restore cached statuses if array has items, otherwise keep all statuses selected
    if (cachedSelectedStatuses && cachedSelectedStatuses.length > 0) {
      setSelectedStatuses(cachedSelectedStatuses);
    }

    // Load cached industry types (industry filter) - CRITICAL for persistence
    // MUST validate against available company industries to prevent stale selection issues
    if (cachedCompanies && cachedCompanies.length > 0) {
      validateAndRestoreIndustryTypes(
        cachedSelectedIndustryTypes,
        cachedCompanies,
        setSelectedIndustryTypes,
        undefined, // Don't update cache here - let later effects handle it
        '[FilterContext:init]'
      );
    } else if (cachedSelectedIndustryTypes && cachedSelectedIndustryTypes.length > 0) {
      // No cached companys to validate against yet, defer to later effects
      console.log('[FilterContext] Cached industry types found but no companies to validate, deferring...');
    }

    setIsLoading(false);
    // Setters are stable useState dispatchers (constant identity), listed only
    // to satisfy exhaustive-deps; the effect still runs once per (rbacReady, user).
  }, [rbacReady, user?.id, setIsLoading, setCompanies, setGroups, setUnitsInternal, setSelectedCompanyIds, setSelectedGroupIds, setSelectedModels, setSelectedStatuses, setSelectedIndustryTypes]);
}

/**
 * Per-selection localStorage-sync effects, extracted verbatim from
 * FilterProvider (ISS-030). MUST be called AFTER the data-loader effects so
 * their registration order relative to the loaders is unchanged.
 */
export function useFilterSelectionSync({
  user,
  hasInitialLoad,
  selectedCompanyIds,
  selectedGroupIds,
  selectedModels,
  selectedStatuses,
  selectedIndustryTypes,
}: FilterSelectionSyncArgs) {
  // Sync selectedCompanyIds to localStorage whenever it changes
  // Uses user-specific cache key to prevent cross-user pollution
  useEffect(() => {
    if (selectedCompanyIds.length > 0 && user?.id) {
      setCachedData(CACHE_KEY_BASE.selectedCompanyIds, user.id, selectedCompanyIds);
    }
  }, [selectedCompanyIds, user?.id]);

  // Sync selectedGroupIds to localStorage whenever it changes
  useEffect(() => {
    if (selectedGroupIds.length > 0 && user?.id) {
      setCachedData(CACHE_KEY_BASE.selectedGroupIds, user.id, selectedGroupIds);
    }
  }, [selectedGroupIds, user?.id]);

  // Sync selectedModels to localStorage whenever it changes
  // CRITICAL: Only save AFTER initial cache load to prevent overwriting cached selection with defaults
  useEffect(() => {
    if (user?.id && hasInitialLoad) {
      setCachedData(CACHE_KEY_BASE.selectedModels, user.id, selectedModels);
    }
  }, [selectedModels, user?.id, hasInitialLoad]);

  // Sync selectedStatuses to localStorage whenever it changes
  // CRITICAL: Only save AFTER initial cache load to prevent overwriting cached selection with defaults
  useEffect(() => {
    if (user?.id && hasInitialLoad) {
      setCachedData(CACHE_KEY_BASE.selectedStatuses, user.id, selectedStatuses);
    }
  }, [selectedStatuses, user?.id, hasInitialLoad]);

  // Sync selectedIndustryTypes to localStorage whenever it changes
  // (was missing - industry types were not being persisted)
  useEffect(() => {
    if (user?.id && hasInitialLoad) {
      setCachedData('company_filter_selected_industry_types', user.id, selectedIndustryTypes);
    }
  }, [selectedIndustryTypes, user?.id, hasInitialLoad]);
}
